<template>
    <div class="media-upload-filepond">
        <div v-if="showMovFileTypeWarning" class="alert alert-warning">
            <i class="fas fa-triangle-exclamation mr-1"></i>
            <strong>Warning:</strong> .mov filetype will be available for download only, it cannot be viewed/played back by by users. For front end playback, please upload .mp4 or .gif. Please contact us for any assistance.
        </div>
        <file-pond
            :id="filepondId"
            name="data"
            ref="pond"
            :files="files"
            :label-idle="options.labelIdle"
            :allow-multiple="options.allowMultiple"
            :allow-file-type-validation="true"
            :allow-image-size-metadata="true"
            :allow-image-exif-orientation="true"
            :allow-browse="options.allowBrowse"
            :allow-drop="options.allowDrop"
            :allow-image-preview="options.allowImagePreview"
            :image-preview-min-height="options.imagePreviewMinHeight"
            :image-preview-max-height="options.imagePreviewMaxHeight"
            :allow-paste="options.allowPaste"
            :allow-remove="options.allowRemove"
            :allow-replace="options.allowReplace"
            :allow-reorder="options.allowReorder"
            :accepted-file-types="acceptedFileTypes"
            label-file-type-not-allowed="Invalid file type"
            file-validate-type-label-expected-types="The selected file type is unsupported."
            :file-validate-type-detect-type="onFileValidateTypeDetectType"
            @init="onInit"
            @addfile="onAddFile"
            @processfile="onProcessFile"
            @processfiles="onProcessFiles"
            @onaddfilestart="trackProcessingFiles(1)"
            @onprocessfileabort="trackProcessingFiles(1)"
            @onerror="trackProcessingFiles(1)"
        />
    </div>
</template>

<script>
import { uniqueId } from 'lodash';
import vueFilePond, { setOptions } from 'vue-filepond';
import { mapGetters, mapMutations, mapActions } from 'vuex';

// Import FilePond styles
import 'filepond/dist/filepond.min.css';

// Import image preview plugin styles
import 'filepond-plugin-image-preview/dist/filepond-plugin-image-preview.min.css';

// Import image preview and file type validation plugins
import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type';
import FilePondPluginImagePreview from 'filepond-plugin-image-preview';
import FilePondPluginImageExifOrientation from 'filepond-plugin-image-exif-orientation';
import FilePondPluginImageSizeMetadata from './filepond-plugin-image-size-metadata';
import FilePondPluginImageTransform from 'filepond-plugin-image-transform';

// Create component
const FilePond = vueFilePond(
    FilePondPluginImageExifOrientation,
    FilePondPluginFileValidateType,
    FilePondPluginImagePreview,
    FilePondPluginImageSizeMetadata,
    FilePondPluginImageTransform,
);

import { MediaType } from 'ca-api-client';

const mediaType =  new MediaType();

export default {
    name: 'FilepondAdapter',
    components: {
        FilePond
    },
    data: () => ({
        showMovFileTypeWarning: false,
    }),
    props: {
        itemId: {
            type: String // for uploading a new version
        },
    },
    computed: {
        ...mapGetters({
            apiUrl: 'getApiUrl',
            files: 'getMediaUploadModalItems',
            options: 'getMediaUploadModalOptions',
        }),
        acceptedFileTypes() {
            let acceptedFileTypes = [];
            if (this.options.allowedFileTypes) {
                acceptedFileTypes = mediaType.getMimeTypesForTypes(this.options.allowedFileTypes).join(',');
            }
            return acceptedFileTypes.length > 0 ? acceptedFileTypes : null;
        },
        filepondId() {
            return uniqueId('filepond_');
        },
    },
    methods: {
        ...mapActions([
            'trackProcessingFiles',
        ]),
        onInit() {
            console.log('MediaUpload - FilePond has initialized');

            setOptions({
                allowRevert: false,
                server: {
                    url: this.apiUrl,
                    process: {
                        url: '/file/upload',
                        method: 'POST',
                        headers: {
                            Authorization: `Bearer ${this.user.token}`,
                        },
                        withCredentials: false,
                        onload: null,
                        onerror: null,
                        ondata: (fd) => { // fd is FormData webapi
                            //const data = fd.getAll('data');

                            fd.append('client_id', this.client.uuid);
                            fd.append('project_id', this.project.uuid);

                            if (this.options.newVersion || this.options.newThumbnail) {
                                fd.append('media_id', this.itemId);
                            }

                            if (this.options.newSetThumbnail) {
                                fd.append('set_id', this.itemId);
                            }

                            if (this.options.newThumbnail || this.options.newSetThumbnail) {
                                fd.append('type', 'thumb');
                            }

                            if (this.options.fileOnly) {
                                fd.append('file_only', 1);
                            }

                            return fd;
                        },
                    },
                },
                imagePreviewCalculateAverageImageColor: false,
                imageTransformImageFilter: (file) => new Promise(resolve => {
                    // no gif mimetype, do transform
                    if (!/image\/gif/.test(file.type)) return resolve(true);

                    const reader = new FileReader();
                    reader.onload = () => {

                        var arr = new Uint8Array(reader.result),
                        i, len, length = arr.length, frames = 0;

                        // make sure it's a gif (GIF8)
                        if (arr[0] !== 0x47 || arr[1] !== 0x49 ||
                            arr[2] !== 0x46 || arr[3] !== 0x38) {
                            // it's not a gif, we can safely transform it
                            resolve(true);
                            return;
                        }

                        for (i=0, len = length - 9; i < len, frames < 2; ++i) {
                            if (arr[i] === 0x00 && arr[i+1] === 0x21 &&
                                arr[i+2] === 0xF9 && arr[i+3] === 0x04 &&
                                arr[i+8] === 0x00 &&
                                (arr[i+9] === 0x2C || arr[i+9] === 0x21)) {
                                frames++;
                            }
                        }

                        // if frame count > 1, it's animated, don't transform
                        if (frames > 1) {
                            return resolve(false);
                        }

                        // do transform
                        resolve(true);
                    }
                    reader.readAsArrayBuffer(file);

                }),
            });
        },
        onAddFile(error, file) {
            console.log('Filepond added file', file);
            if (file.fileType === 'video/quicktime') {
                this.showMovFileTypeWarning = true
            }
            if (error) return;
        },
        onProcessFile(error, file) {
            this.trackProcessingFiles(-1);
            let retVal = null;
            const callback = this.options.onProcessFile;
            if (callback && callback instanceof Function) retVal = callback(file);
            this.$emit('file-processed', retVal);
        },
        onProcessFiles() {
            let retVal = null;
            const callback = this.options.onProcessFiles;
            if (callback && callback instanceof Function) retVal = callback();
            this.$emit('files-processed', retVal);
        },
        onFileValidateTypeDetectType(source, type) {
            return new Promise((resolve, reject) => {
                if (!type) {
                    const ext = source.name.split('.').pop();
                    const mimeType = mediaType.getMimeTypeFromExtension(ext);
                    console.log('Filepond onFileValidateTypeDetectType', ext, mimeType);
                    if (mimeType) {
                        resolve(mimeType);
                    } else {
                        reject();
                    }
                }
                resolve(type);
            });
         },
    }
}
</script>
